/**
 * OpportunitiesTable.jsx
 *
 * Shared opportunities table (styled like the Projects table) used by:
 *   - OpportunitiesView (workspace-wide pipeline)
 *   - CRMView's account → Opportunities tab (pre-filtered to one customer)
 *
 * Visual + filter parity with the Projects table:
 *   - Bold headers on a pale-green band with thick dark-green underline
 *   - Funnel-SVG filter button on EVERY data column
 *   - Date columns use DateFilterPopover with period presets (Last Week
 *     / Month / 3mo / 6mo / Year / This Year + custom range)
 *   - Pixel min-widths so cells never get crushed below readable size
 *   - Instant row hover (no easing) so all cells highlight together
 *
 * Props:
 *   opps             — array of opportunity (= project) records, already filtered to scope
 *   onEdit(o)        — open an edit modal for this opportunity
 *   onMarkWon(o)     — confirm marking won
 *   onMarkLost(o)    — confirm marking lost (another company won it)
 *   onMarkCancelled(o) — confirm marking cancelled (cancelled or not awarded)
 *   onMarkNoQuote(o) — confirm marking No Quote (we did not quote it)
 *   (the four sit in one "Close as..." dropdown per open row; all but Won ask why)
 *   updateProject    — passthrough for the Quote Sent toggle
 *   onToast(msg)     — surface a toast
 *   title            — section title
 *   defaultStatus    — initial status filter chip (default 'Open')
 *   tableKey         — whose saved views to use ('opportunities', or
 *                      'ksa-opportunities' on GLOBAL KSA). Without one (an
 *                      account's tab) the table shows its default columns and
 *                      has no views bar.
 *
 * Customizable per person (Sharif, 10/9/26): columns shown / hidden /
 * reordered (opportunities/opportunityColumns.js is the list), highlight
 * rules, saved views (components/tableViews/). A totals row - Quoted summed,
 * % Prob averaged over the filtered rows - is pinned under the title strip
 * and repeated at the bottom like the Projects table's, and follows the
 * columns. Export CSV writes exactly what is on screen; Export Excel the
 * same as an .xlsx with the highlight colours (round 2, 10/9/26). Headers
 * can be dragged to reorder and resized from their right edge (saved with
 * the view) - only with a tableKey, so an account's tab is unchanged.
 *
 * Group by main project (10/14/26, as on the Projects tab): records that share
 * a main project (data/projectHierarchy - the Global Project Number, or a
 * record's Main project) sit under it; a group row sums the Quoted of its
 * records and opens / closes on a click. Totals and exports stay the records
 * (the export adds a Main project column). Not on GLOBAL KSA.
 */

import { useState, useMemo, useCallback } from 'react';
import { Icon } from './ui/Icon';
import { Button } from './ui/Button';
import { Chip } from './ui/Chip';
import { SearchInput } from './ui/SearchInput';
import { Th as BidTh, Td, shortDate, renderFilterPopover } from './ui/BidTablePrimitives';
import { StatusPill } from './ui/StatusPill';
import { dayKey } from './ui/DateFilterPopover';
import { useApp } from '../context/AppContext';
import { useSyncedStatusChips } from '../hooks/useSyncedStatusChips';
import { fmt, fmtMoney } from '../utils/format';
import { useDataScope } from '../context/DataScope';
import { fmtMoney as ksaFmtMoney, fxCode } from './ksa/ksaMoney';
import TasksCell from './tasks/TasksCell';
import QuickAddMenu from './tasks/QuickAddMenu';
import { useProjectTasksIndex } from './tasks/useTasks';
import CellDocLink from './shared/CellDocLink';
import CommentsCell from './shared/CommentsCell';
import { useDocIndex } from '../hooks/useDocIndex';
import { useCommentsIndex } from '../hooks/useCommentsIndex';
import { exportCsv } from '../utils/exportCsv';
import { csvMoney2, csvText } from '../utils/csv';
import { OPPORTUNITY_COLUMNS } from './opportunities/opportunityColumns';
import { useTableView } from './tableViews/useTableView';
import TableViewsBar from './tableViews/TableViewsBar';
import TotalsRow, { usePinnedHeight } from './tableViews/TotalsRow';
import { rowHighlights, highlightStyle, cellLook, hoverProps } from './tableViews/highlights';
import { viewWorkbook, downloadXlsx } from './tableViews/xlsxExport';
import { exportLevelLook } from './tableViews/treeLook';
import { usePointerSort } from './tableViews/usePointerSort';
import { lookOf, alignOf, usePinnedLefts, pinStyle, actionsWidth } from './tableViews/tableLook';
import { tableLayout } from './tableViews/viewConfig';
import { exportViewRows } from './tableViews/exportView';
import { rollupRow } from '../data/projectHierarchy';
import { useProjectTree } from './projects/hierarchy/useProjectTree';
import { TreeControls, TreeGroupLabel, TreeIndent, groupLook, matchStyle } from './projects/hierarchy/TreeParts';

// Cancelled = not awarded or cancelled (it was "Dead" 9/29 to 10/1/26, Paul
// Q-01); No Quote = we backed out (Paul Q5). Closed = Lost + Cancelled + No
// Quote. Both take the muted chip tone; the status pill in the row has its
// own colour. There is no Budget chip: that status was folded into Open on 9/29/26.
const STATUS_LIST = ['Open', 'Won', 'Lost', 'Cancelled', 'No Quote'];
const STATUS_TONE = { Won: 'win', Open: 'open', Lost: 'lost', Cancelled: 'cancel', 'No Quote': 'cancel' };

const DATE_COLS = new Set(['quoteDate', 'bookDate', 'billDate']);
const DEFAULT_SORT = { col: 'quoteDate', dir: 'desc' };
const blankOr = (v) => (v == null || v === '' ? null : Number(v));
// CSV: $ values with 2 decimals; quote numbers wrapped ="..." so Excel keeps leading zeros (as Projects)

// Module-scope wrapper (stable component identity — never define this inside a
// render body) that pins every header cell to the top of the table's scroll
// pane. Same fix as the Projects bid table; see the pane comment below.
const Th = (props) => <BidTh sticky {...props} />;

export function OpportunitiesTable({
  opps,
  onEdit,
  onMarkWon,
  onMarkLost,
  onMarkCancelled,
  onMarkNoQuote,
  updateProject,
  onToast,
  title,
  defaultStatus = 'Open',
  tableKey = null,
}) {
  const { resolveOwnerName, allProjects } = useApp();
  const scope = useDataScope();
  // KSA amounts carry their record's currency code (SAR 480,000.00), never a "$"
  const money = (v, p) => (scope ? (Number(v) > 0 ? ksaFmtMoney(v, fxCode(p && p.currency) || 'SAR') : '-') : fmt(v));
  const moneyText = (v, p) => (scope ? (v != null && v !== '' ? ksaFmtMoney(v, fxCode(p && p.currency) || 'SAR') : '') : fmtMoney(v));
  // Chips are a thin UI over columnFilters — seeded from defaultStatus.
  const [defaultFilters] = useState(() => (defaultStatus && defaultStatus !== 'All' ? { status: new Set([defaultStatus]) } : {}));
  const { columnFilters, setColumnFilters, setChip, chipValue } = useSyncedStatusChips(() => defaultFilters);
  const statusChip = chipValue('status') || 'All';
  const [openFilter, setOpenFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(DEFAULT_SORT);
  // The person's columns, highlights and saved views (none without a tableKey).
  const view = useTableView({
    tableKey,
    defs: OPPORTUNITY_COLUMNS,
    columnFilters,
    setColumnFilters,
    sort,
    setSort,
    search,
    setSearch,
    defaultFilters,
    defaultSort: DEFAULT_SORT,
    grouping: !scope, // GLOBAL KSA lists its records flat
  });
  // ONE fetch of every project task, indexed by row id - the Tasks column reads
  // this instead of firing a request per row. Bumping taskVersion re-pulls it.
  const [taskVersion, setTaskVersion] = useState(0);
  const taskIndex = useProjectTasksIndex(taskVersion);
  const [quickTarget, setQuickTarget] = useState(null); // right-click quick add
  // Quote # opens its attached quote file, same index the Projects table uses
  // for PO/invoice numbers — one source so the two can never disagree.
  const docIdFor = useDocIndex();
  // One fetch for the Comments column, same as the Tasks column's index.
  const commentIndex = useCommentsIndex();

  // Every visible column gets an accessor so it can be filtered through the popover.
  // shared money formatter lives in utils/format.js
  const COL_ACCESSORS = useMemo(
    () => ({
      quoteDate: (p) => p.quoteDate || p.quote_date,
      quoteLetter: (p) => p.quoteLetter || p.quote_letter,
      rev: (p) => p.rev,
      description: (p) => p.description || p.name,
      customer: (p) => p.customer,
      subCustomer: (p) => p.subCustomer || p.sub_customer,
      type: (p) => p.type,
      engineer: (p) => p.engineer,
      approvalManager: (p) => p.approvalManager || p.approval_manager,
      owner: (p) => resolveOwnerName(p.owner),
      poNumber: (p) => p.poNumber || p.po_number,
      quotedPrice: (p) => moneyText(p.quotedPrice ?? p.quoted_price, p),
      bookDate: (p) => p.bookDate || p.book_date,
      billDate: (p) => p.billDate || p.bill_date,
      probability: (p) => (p.probability == null ? null : `${p.probability}%`),
      quoteType: (p) => p.quoteType || p.quote_type,
      quoteSent: (p) => (p.quoteSent || p.quote_sent ? 'Yes' : 'No'),
      status: (p) => p.status,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- moneyText follows the scope, fixed for the table's life
    [resolveOwnerName]
  );

  const filtered = useMemo(() => {
    const rows = (opps || []).filter((p) => {
      // Status chips live inside columnFilters, so this loop covers them too.
      for (const [colId, value] of Object.entries(columnFilters)) {
        if (!value) continue;
        const accessor = COL_ACCESSORS[colId];
        if (!accessor) continue;
        if (DATE_COLS.has(colId)) {
          // Date range: { from, to } in YYYY-MM-DD
          const v = accessor(p);
          if (!v) return false;
          const s = dayKey(v); // 2/14/2024 as well as 2024-02-14
          if (!s) return false;
          if (value.from && s < value.from) return false;
          if (value.to && s > value.to) return false;
          continue;
        }
        if (!value.size) continue;
        const v = accessor(p);
        if (!value.has(v == null ? '' : String(v))) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        // Search every visible column (accessors) + raw fallbacks
        const hay = Object.values(COL_ACCESSORS)
          .map((a) => a(p))
          .concat([p.name, p.sub_customer])
          .filter((v) => v != null && v !== '')
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return rows.sort((a, b) => {
      const av = a[sort.col];
      const bv = b[sort.col];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [opps, columnFilters, search, sort, COL_ACCESSORS]);

  // main project > sub-project > PO line over the rows on screen (useProjectTree)
  const tree = useProjectTree({
    tableKey: tableKey || 'account-opportunities',
    view,
    rows: filtered,
    source: opps,
    lookup: allProjects,
    search,
  });
  const treeCol = view.visible.some((c) => c.col === 'description') ? 'description' : view.visible[0] && view.visible[0].col;

  const columnUniqueValues = useMemo(() => {
    const cols = {};
    for (const [colId, accessor] of Object.entries(COL_ACCESSORS)) {
      if (DATE_COLS.has(colId)) continue;
      const counts = new Map();
      (opps || []).forEach((p) => {
        const v = accessor(p);
        if (v == null || v === '') return;
        const k = String(v);
        counts.set(k, (counts.get(k) || 0) + 1);
      });
      cols[colId] = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, count }));
    }
    return cols;
  }, [opps, COL_ACCESSORS]);

  // Totals of the rows on screen (10/9/26): Quoted summed, % Prob averaged.
  // KSA amounts are summed only within one currency.
  const totals = useMemo(() => {
    const sumQuoted = filtered.reduce((s, p) => s + (Number(p.quotedPrice ?? p.quoted_price) || 0), 0);
    const probs = filtered.map((p) => p.probability).filter((v) => v != null && v !== '' && !Number.isNaN(Number(v)));
    const avgProb = probs.length ? probs.reduce((s, v) => s + Number(v), 0) / probs.length : null;
    return { count: filtered.length, sumQuoted, avgProb };
  }, [filtered]);
  const totalsCode = useMemo(() => {
    if (!scope) return null;
    const codes = new Set(filtered.map((r) => fxCode(r.currency) || 'SAR'));
    return codes.size <= 1 ? [...codes][0] || 'SAR' : 'mixed currencies';
  }, [scope, filtered]);
  const totalsMoney = (v) => (scope ? (Number(v) > 0 ? ksaFmtMoney(v, totalsCode) : '-') : fmt(v));
  const totalsCols = [...view.visible.map((c) => c.col), 'actions'];
  const totalCells = {
    quotedPrice: { content: totalsMoney(totals.sumQuoted) },
    probability: { content: totals.avgProb == null ? '-' : `avg ${Math.round(totals.avgProb)}%` },
  };
  const shownTotals = Object.fromEntries(Object.entries(totalCells).filter(([c]) => totalsCols.includes(c)));
  const [totalsRef, totalsH] = usePinnedHeight(`${filtered.length}:${view.visible.length}`);
  // the view's look (tableViews/tableLook.js): where the totals show, the sticky header ...
  const look = lookOf(view);
  const showTop = filtered.length > 0 && look.totalsTop;
  const showBottom = filtered.length > 0 && look.totalsBottom;
  const headTop = showTop && look.sticky ? totalsH : 0;

  // A column's value for the highlight rules: numbers as numbers (null when blank), dates as stored, else the text.
  const rawValue = (p, col) => {
    if (col === 'quotedPrice') return blankOr(p.quotedPrice ?? p.quoted_price);
    if (col === 'probability') return blankOr(p.probability);
    return COL_ACCESSORS[col] ? COL_ACCESSORS[col](p) : null;
  };

  // Export = what is on screen: the visible columns in order, the filtered and sorted rows, then the totals.
  const exportValue = (p, col) => {
    switch (col) {
      case 'quoteDate':
        return p.quoteDate || p.quote_date || '';
      case 'quoteLetter':
        return csvText(p.quoteLetter || p.quote_letter);
      case 'quotedPrice':
        return csvMoney2(p.quotedPrice ?? p.quoted_price);
      case 'probability':
        return p.probability == null ? '' : `${p.probability}%`;
      case 'bookDate':
        return p.bookDate || p.book_date || '';
      case 'billDate':
        return p.billDate || p.bill_date || '';
      default: {
        const v = COL_ACCESSORS[col] ? COL_ACCESSORS[col](p) : '';
        return v == null ? '' : String(v);
      }
    }
  };
  const exportName = (ext) => `globalops_${scope ? 'ksa_' : ''}opportunities_${new Date().toISOString().slice(0, 10)}.${ext}`;
  const exportTotals = {
    totals: { quotedPrice: csvMoney2(totals.sumQuoted), probability: totals.avgProb == null ? '' : `avg ${Math.round(totals.avgProb)}%` },
    totalsLabel: `Totals (${totals.count} records)`,
  };
  // grouped: the records flat in the tree's order with a Main project column first (a group row is never exported)
  const MAIN_COL = { col: 'mainProject', label: 'Main project', kind: 'text', minWidth: 160 };
  const exportRows =
    tree.on && tree.nodes
      ? tree.nodes.flatMap(function leaves(n) {
          return n.type === 'row' ? [n.record] : n.children.flatMap(leaves);
        })
      : filtered;
  const exportCols = tree.on && tree.groupCount > 0 ? [MAIN_COL, ...view.visible] : view.visible; // only when something is grouped
  const exportCell = (p, col) => (col === 'mainProject' ? tree.mainLabel(p) : exportValue(p, col));
  const handleExportCsv = () => exportCsv(exportName('csv'), exportViewRows(exportCols, exportRows, exportCell, exportTotals));
  // The same rows as an .xlsx with the highlight colours; KSA money keeps each record's currency.
  const handleExportXlsx = () => {
    const { bytes } = viewWorkbook(
      exportCols !== view.visible ? { ...view, visible: exportCols } : view,
      exportRows,
      exportCell,
      (p, col) => (col === 'mainProject' ? tree.mainLabel(p) : rawValue(p, col)),
      {
        ...exportTotals,
        sheetName: scope ? 'KSA Opportunities' : 'Opportunities',
        currencyOf: (p) => (scope ? fxCode(p.currency) || 'SAR' : null),
        totalsCurrency: scope ? totalsCode : null,
        // a main project's / sub-project's own record carries its group row's colour (Customize > Group rows)
        rowLook: tree.on
          ? (p) => {
              const lv = tree.levelOf(p.id);
              return lv ? exportLevelLook(view.tree, lv) : null;
            }
          : null,
      }
    );
    downloadXlsx(exportName('xlsx'), bytes);
  };
  // drag a header onto another to move it there (views on only)
  // drag a header to move its column (views on only; tableViews/usePointerSort)
  const headSort = usePointerSort({ axis: 'x', indicator: true, onDrop: (col, to) => view.reorder(col, to, 'visible') });
  // Resized columns (round 3): fixed layout at the person's widths, Actions 110px
  // The Actions cell is sized to its buttons and never squeezed by saved
  // widths (round 4: "Lost" was cut off at the table's edge)
  const ACTIONS_W = actionsWidth({ won: !!onMarkWon, lost: !!onMarkLost, cancelled: !!onMarkCancelled, noQuote: !!onMarkNoQuote });
  // the "Close as..." choices this table offers, in order
  const closeChoices = [
    ['Won', onMarkWon],
    ['Lost', onMarkLost],
    ['Cancelled', onMarkCancelled],
    ['No Quote', onMarkNoQuote],
  ].filter(([, fn]) => fn);
  const tl = tableLayout(view.visible, view.widths, ACTIONS_W);
  const pins = usePinnedLefts(view, (col) => tl.widthOf(view.visible.find((c) => c.col === col) || {}));
  const headRef = (el) => {
    headSort.ref.current = el;
    pins.rowRef.current = el;
  };
  // the whole row under the mouse, in the view's colour (index.css .tv-hover)
  const hov = hoverProps(view.hover);
  // a cell's own font size, and its full value as a tooltip when an ellipsis may cut it
  const cellExtra = (p, col, own) => {
    const size = view.fonts[col] && view.fonts[col].cell;
    const title = (tl.fixed || !look.wrap) && !own && COL_ACCESSORS[col] ? String(COL_ACCESSORS[col](p) ?? '') || undefined : own;
    return { style: size ? { fontSize: size } : null, title };
  };

  const statusCounts = useMemo(() => {
    const out = { All: opps?.length || 0 };
    for (const s of STATUS_LIST) {
      out[s] = (opps || []).filter((p) => (p.status || '').toLowerCase() === s.toLowerCase()).length;
    }
    return out;
  }, [opps]);

  const toggleSort = useCallback(
    (col) => setSort((cur) => (cur.col === col ? { col, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' })),
    []
  );
  const sortIndicator = (col) => (sort.col === col ? <span className="ml-1 text-gat-green">{sort.dir === 'asc' ? '↑' : '↓'}</span> : null);

  const handleToggleQuoteSent = async (p) => {
    if (!updateProject) return;
    const next = !(p.quoteSent || p.quote_sent);
    await updateProject(p.id, { quoteSent: next });
    if (onToast) onToast(`Quote Sent: ${next ? 'Yes' : 'No'}`);
  };

  // One cell per column id, so the person's order and hidden columns decide what a row shows.
  const rowCells = (p) => {
    const B = 'border-r border-[#E8E8E8]';
    const empty = <span className="text-text-empty">-</span>;
    const qs = !!(p.quoteSent || p.quote_sent);
    return {
      quoteDate: { className: `${B} font-semibold whitespace-nowrap`, children: shortDate(p.quoteDate || p.quote_date) || '-' },
      quoteLetter: {
        className: `font-mono text-[10px] text-text-body ${B} whitespace-nowrap`,
        children:
          p.quoteLetter || p.quote_letter ? (
            <CellDocLink attId={docIdFor(p.id, 'quote', p.quoteLetter || p.quote_letter)} text={p.quoteLetter || p.quote_letter} />
          ) : (
            empty
          ),
      },
      rev: { className: `${B} text-center font-mono text-[10px] text-text-body whitespace-nowrap`, children: p.rev || empty },
      customer: { className: `${B} break-words`, children: p.customer || '-' },
      subCustomer: { className: `${B} text-text-body break-words`, children: p.subCustomer || p.sub_customer || empty },
      description: {
        className: `${B} text-[10.5px] leading-snug break-words`,
        title: p.description || p.name || '',
        children: p.description || p.name || '-',
      },
      tasks: { className: B, children: <TasksCell tasks={taskIndex.get(p.id)} /> },
      comments: { className: B, children: <CommentsCell comments={commentIndex.get(p.id)} /> },
      type: { className: `${B} text-text-body break-words`, children: p.type || empty },
      engineer: { className: `${B} break-words`, children: p.engineer || empty },
      approvalManager: { className: `${B} text-text-body break-words`, children: p.approvalManager || p.approval_manager || empty },
      owner: { className: `${B} break-words`, children: resolveOwnerName(p.owner) || empty },
      quotedPrice: {
        num: true,
        className: `${B} font-semibold whitespace-nowrap`,
        children: p.quotedPrice ? money(p.quotedPrice, p) : '-',
      },
      bookDate: { className: `${B} whitespace-nowrap`, children: shortDate(p.bookDate || p.book_date) || empty },
      billDate: { className: `${B} whitespace-nowrap`, children: shortDate(p.billDate || p.bill_date) || empty },
      probability: { num: true, className: `${B} whitespace-nowrap`, children: p.probability != null ? `${p.probability}%` : empty },
      quoteType: { className: `${B} whitespace-nowrap`, children: p.quoteType || p.quote_type || empty },
      quoteSent: {
        className: `${B} text-center`,
        children: (
          <div className="inline-flex rounded-md overflow-hidden border border-[#D0D0D0] text-[10px] font-bold">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!qs) handleToggleQuoteSent(p);
              }}
              className={['px-2 py-0.5', qs ? 'bg-emerald-600 text-white' : 'bg-white text-text-muted hover:bg-emerald-50'].join(' ')}
              aria-pressed={qs}
            >
              Yes
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (qs) handleToggleQuoteSent(p);
              }}
              className={[
                'px-2 py-0.5 border-l border-[#D0D0D0]',
                !qs ? 'bg-rose-100 text-rose-800' : 'bg-white text-text-muted hover:bg-rose-50',
              ].join(' ')}
              aria-pressed={!qs}
            >
              No
            </button>
          </div>
        ),
      },
      status: { className: B, children: <StatusPill status={p.status} /> },
    };
  };

  return (
    <div className="space-y-3">
      {/* right-click a row → add a task or comment without opening the record */}
      <QuickAddMenu
        target={quickTarget}
        onClose={() => setQuickTarget(null)}
        onAdded={(msg) => {
          setTaskVersion((v) => v + 1);
          if (onToast) onToast(msg);
        }}
      />
      <div className="bg-surface border border-border rounded-md shadow-sm px-3.5 py-2.5 flex items-center gap-2 flex-wrap">
        <Chip on={statusChip === 'All'} onClick={() => setChip('status', null)}>
          All ({statusCounts.All})
        </Chip>
        {STATUS_LIST.map((s) => (
          <Chip key={s} on={statusChip === s} tone={STATUS_TONE[s]} onClick={() => setChip('status', statusChip === s ? null : s)}>
            {s} ({statusCounts[s] || 0})
          </Chip>
        ))}
        <span className="w-px h-5 bg-border-strong mx-1" />
        <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search any column…" className="w-56" />
        <TreeControls tree={tree} />
        {tableKey && (
          <TableViewsBar view={view} defs={OPPORTUNITY_COLUMNS} hasGroups={tree.available}>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleExportCsv}
              iconLeft={<Icon name="download" size={12} color="currentColor" />}
            >
              Export CSV
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleExportXlsx}
              iconLeft={<Icon name="download" size={12} color="currentColor" />}
            >
              Export Excel
            </Button>
          </TableViewsBar>
        )}
      </div>

      <div className="bg-surface border border-border rounded-lg shadow-md overflow-hidden">
        <div className="bg-gat-greenDarkest text-white px-4 py-3 flex items-center justify-between">
          <h3 className="text-h3 m-0">{title || (statusChip === 'All' ? 'All Opportunities' : `${statusChip} Opportunities`)}</h3>
          <div className="text-meta text-white/55 tabular-nums">
            {filtered.length} visible · sorted by {sort.col} {sort.dir === 'asc' ? '↑' : '↓'}
          </div>
        </div>
        {/*
          Vertical scrolling for the table happens in THIS pane, not on the page:
          the wrapper is already a scroll box (overflow-x:auto forces overflow-y
          to compute to `auto`) and `position: sticky` resolves against the
          nearest scroll box, so the header can only pin once this box has a
          capped height. Sticking to the page scroller is impossible from here —
          the surrounding card is `overflow-hidden`, itself a scroll box.
          290px ≈ the chrome above/below the pane (64 topbar + 24/24 page padding
          + page heading + chip bar + the card's green title strip).
        */}
        <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 290px)' }}>
          {/* Pixel min-widths so cells never crush; min-w-[1200px] table + overflow-x-auto wrapper falls back to horizontal scroll on narrow viewports. Instant hover via group + [&_td]:transition-none. */}
          <table
            style={{ ...tl.style, ...hov.style, ...look.tableStyle }}
            className={`${hov.className} ${look.tableClass} ${tl.fixed ? 'tv-fixed' : 'w-full min-w-[1200px]'} border-collapse text-[10.5px] [&_td]:transition-none`}
          >
            {/* one <col> per column on screen, in the person's order (opportunityColumns.js has the widths) */}
            <colgroup>
              {view.visible.map((c) => {
                const w = tl.widthOf(c); // fixed once a column was resized (viewConfig.tableLayout)
                return <col key={c.col} style={w ? { width: w, minWidth: w, maxWidth: w } : { minWidth: c.minWidth }} />;
              })}
              <col style={tl.fixed ? { width: ACTIONS_W } : { minWidth: ACTIONS_W }} /> {/* Actions - Close as... */}
            </colgroup>
            <thead>
              {/* Pinned totals bar first; the column headers stick just below it. */}
              {showTop && (
                <TotalsRow
                  cols={totalsCols}
                  cells={shownTotals}
                  label={{ title: 'Totals', count: totals.count }}
                  stickyTop={look.sticky ? 0 : undefined}
                  rowRef={totalsRef}
                  pad="px-1.5 py-1.5"
                />
              )}
              <tr ref={headRef} onPointerDown={view.enabled ? headSort.onPointerDown : undefined}>
                {/* noFilter columns (Tasks, Comments) have no accessor, so they get no funnel */}
                {view.visible.map((c) => {
                  const isDate = DATE_COLS.has(c.col);
                  const fv = columnFilters[c.col];
                  const filterActive = isDate ? !!(fv && (fv.from || fv.to)) : !!(fv && fv.size > 0);
                  return (
                    <Th
                      key={c.col}
                      col={c.col}
                      sticky={look.sticky}
                      stickyTop={headTop}
                      pinLeft={pins.isPinned(c.col) ? pins.lefts[c.col] || 0 : undefined}
                      align={view.align[c.col]}
                      onSort={c.sortable ? toggleSort : undefined}
                      num={c.num}
                      filterable={!c.noFilter}
                      filterActive={filterActive}
                      onFilterClick={c.noFilter ? undefined : () => setOpenFilter(openFilter === c.col ? null : c.col)}
                      {...(view.enabled
                        ? {
                            className: pins.last === c.col ? 'tv-pin-edge' : '',
                            width: tl.widthOf(c),
                            fontSize: view.fonts[c.col] && view.fonts[c.col].head,
                            onResize: (px, measured) => view.setWidth(c.col, px, measured),
                            onResizeReset: () => view.clearWidth(c.col),
                            resizeLabel: c.label,
                            drag: { 'data-sort-key': c.col, 'data-sort-label': c.label },
                          }
                        : {})}
                    >
                      {c.label}
                      {c.sortable && sortIndicator(c.col)}
                      {openFilter === c.col &&
                        renderFilterPopover({
                          colId: c.col,
                          columnLabel: c.label,
                          isDate,
                          fv,
                          values: columnUniqueValues[c.col],
                          setColumnFilters,
                          sort,
                          setSort,
                          onClose: () => setOpenFilter(null),
                        })}
                    </Th>
                  );
                })}
                <Th col="actions" sticky={look.sticky} stickyTop={headTop} />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={totalsCols.length} className="text-center text-text-muted text-[12px] py-10">
                    No opportunities match the current filters.
                  </td>
                </tr>
              )}
              {(tree.items || filtered.map((p) => ({ node: { type: 'row', key: p.id, record: p }, depth: 0 }))).map((item, rowIdx) => {
                if (item.node.type === 'group') {
                  // a main project / sub-project: its records' Quoted summed; a click opens / closes it
                  const n = item.node;
                  const g = rollupRow(n.records, { key: n.key, level: n.level, gpn: n.gpn, label: n.label, total: n.total });
                  const cells = rowCells(g);
                  const gl = groupLook(item, view.tree); // the view's group-row look (Customize > Group rows)
                  // highlight rules (Customize > Highlights) on the group's sums, cell rules only - the band stays the group's
                  const ghl = view.highlights.length ? rowHighlights(view.highlights, (col) => rawValue(g, col)) : null;
                  return (
                    <tr
                      key={`g:${n.key}`}
                      {...gl.rowProps}
                      className="border-b border-[#C9D6BF] cursor-pointer"
                      onClick={() => tree.toggle(n.key)}
                      title={item.open ? 'Collapse' : 'Expand'}
                    >
                      {view.visible.map(({ col }) => {
                        const c = cells[col];
                        const blankCell =
                          col === 'tasks' ||
                          col === 'comments' ||
                          col === 'quoteSent' ||
                          (col === 'status' && !g.status) ||
                          (c.num && !gl.totals);
                        const hs = ghl && !blankCell ? highlightStyle(ghl.cells[col] || null) : null;
                        return (
                          <Td
                            key={col}
                            hl={!!(hs && hs.background)}
                            num={c.num}
                            className={`${c.className || ''}${pins.last === col ? ' tv-pin-edge' : ''}`}
                            style={{ ...alignOf(view, col), ...pinStyle(pins, col), ...gl.cellStyle, ...hs }}
                          >
                            {col === treeCol ? (
                              <TreeGroupLabel item={item} onToggle={tree.toggle} tree={view.tree} />
                            ) : blankCell ? null : (
                              c.children
                            )}
                          </Td>
                        );
                      })}
                      <Td className="tv-actions" />
                    </tr>
                  );
                }
                const p = item.node.record;
                const cells = rowCells(p);
                const hl = view.highlights.length ? rowHighlights(view.highlights, (col) => rawValue(p, col)) : null;
                const hlOf = (col) => highlightStyle(cellLook(hl, col));
                const hit = matchStyle(item);
                return (
                  <tr
                    key={p.id}
                    onClick={() => onEdit && onEdit(p)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setQuickTarget({ row: p, x: e.clientX, y: e.clientY });
                    }}
                    data-highlight={hl && hl.row ? 'row' : undefined}
                    data-depth={item.depth || undefined}
                    className={[
                      // Instant hover: every cell flips to emerald at the same moment.
                      'group border-b border-[#D8DED4]',
                      onEdit ? 'cursor-pointer' : '',
                      // the hover itself is the table's (index.css .tv-hover), so it can be switched off
                      rowIdx % 2 === 0 || !look.zebra ? 'bg-white' : 'bg-[#EEF2E9]',
                    ].join(' ')}
                  >
                    {view.visible.map(({ col }) => {
                      const c = cells[col];
                      const x = cellExtra(p, col, c.title);
                      const hs = hlOf(col);
                      const al = alignOf(view, col);
                      const pin = pinStyle(pins, col);
                      const mark = col === treeCol ? hit : null;
                      return (
                        <Td
                          key={col}
                          hl={!!(hs && hs.background)}
                          num={c.num}
                          className={`${c.className || ''}${pins.last === col ? ' tv-pin-edge' : ''}${pin ? ' tv-pin' : ''}`}
                          title={x.title}
                          style={hs || x.style || al || pin || mark ? { ...x.style, ...al, ...pin, ...mark, ...hs } : undefined}
                        >
                          {col === treeCol && item.depth > 0 ? (
                            <TreeIndent item={item} lines={view.tree.lines}>
                              {c.children}
                            </TreeIndent>
                          ) : (
                            c.children
                          )}
                        </Td>
                      );
                    })}
                    <Td
                      style={hlOf('actions')}
                      hl={!!(hlOf('actions') && hlOf('actions').background)}
                      className="tv-actions whitespace-nowrap"
                    >
                      <div className="flex gap-1 items-center">
                        {/* No Edit button: clicking anywhere on the row already opens
                            the editor, so it only repeated that and crowded Won / Lost. */}
                        {(p.status || '').toLowerCase() === 'open' && closeChoices.length > 0 && (
                          // ponytail: a native select - compact, keyboard-friendly, never clipped by the scrolling table
                          <select
                            value=""
                            aria-label={`Close ${p.name || 'opportunity'} as`}
                            title="Close as Won, Lost, Cancelled or No Quote"
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              const fn = closeChoices.find(([s]) => s === e.target.value);
                              if (fn) fn[1](p);
                            }}
                            className="bg-white border border-[#D6D9CF] rounded-md px-1.5 py-[3px] cursor-pointer hover:border-[#9AA58F] text-[10px] font-bold text-[#444]"
                          >
                            <option value="" disabled>
                              Close as…
                            </option>
                            {closeChoices.map(([s]) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })}
              {showBottom && (
                <TotalsRow cols={totalsCols} cells={shownTotals} label={{ title: 'Totals', count: totals.count }} pad="px-1.5 py-1.5" />
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
